/**
 * Partition Tab
 *
 * Enables splitting Smart Asset units into a new allocation.
 */
import React from "react";
import type { UsePartitionStateReturn } from "../../../../hooks";
import type { SmartAssetData } from "../SmartAssetCard";
import { DEMO_CONTACTS } from "../../../../data/demoContacts";

const PARTITION_CONTACTS = DEMO_CONTACTS.filter((contact) => contact.rgbAddress);

interface PartitionTabProps {
  asset: SmartAssetData;
  totalUnits: number;
  partitionState: UsePartitionStateReturn;
}

export const PartitionTab: React.FC<PartitionTabProps> = ({
  asset,
  totalUnits,
  partitionState,
}) => {
  const {
    sourceUnits,
    newUnits,
    selectedDestination,
    sourcePercent,
    newPercent,
    dustLimitReached,
    canPartition,
    confirmDisabled,
    inputMin,
    inputMax,
    setSourceUnits,
    setNewUnits,
    setSelectedDestination,
  } = partitionState;

  // Address placeholders
  const sourceAddress = "RGB:bc1p_Your_Current_Allocation...";
  const newAddress =
    selectedDestination === "generate"
      ? "Will be generated..."
      : (PARTITION_CONTACTS.find((c) => c.id === selectedDestination)?.rgbAddress ?? "—");

  return (
    <div
      className="bg-sa-surface border border-sa-border rounded-lg p-6"
      data-testid="smart-asset-partition-content"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
          <svg
            className="w-5 h-5 text-orange-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Partition {asset.name} Smart Asset Units</h2>
          <p className="text-sm text-sa-text-muted">
            Split units into a new Smart Asset allocation.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="rounded-xl border border-orange-500/40 bg-sa-surface-hover p-4">
          <p className="text-sm font-semibold text-orange-500 mb-1">Source</p>
          <p className="text-xs text-sa-text-muted mb-3">{sourceAddress}</p>
          <label className="text-xs text-sa-text-muted block mb-1">Units</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={inputMin}
              max={inputMax}
              value={sourceUnits}
              onChange={(event) => {
                const nextValue = Number(event.target.value);
                setSourceUnits(Number.isNaN(nextValue) ? 0 : nextValue);
              }}
              disabled={!canPartition}
              data-testid="smart-asset-partition-source-input"
              className="w-full rounded-lg border border-sa-border bg-sa-surface px-3 py-2 text-sm text-sa-text"
            />
            <span className="text-xs text-sa-text-muted">Units</span>
          </div>
        </div>

        <div className="rounded-xl border border-orange-500/40 bg-sa-surface-hover p-4">
          <p className="text-sm font-semibold text-orange-500 mb-1">New</p>
          <p className="text-xs text-sa-text-muted mb-2">{newAddress}</p>
          <label className="text-xs text-sa-text-muted block mb-1">Destination Account</label>
          <select
            value={selectedDestination}
            onChange={(event) => setSelectedDestination(event.target.value)}
            data-testid="smart-asset-partition-destination-select"
            className="w-full rounded-lg border border-sa-border bg-sa-surface px-3 py-2 text-sm text-sa-text mb-3"
          >
            <option value="generate">Generate new address</option>
            {PARTITION_CONTACTS.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.name} · {contact.rgbAddress}
              </option>
            ))}
          </select>
          <label className="text-xs text-sa-text-muted block mb-1">Units</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={inputMin}
              max={inputMax}
              value={newUnits}
              onChange={(event) => {
                const nextValue = Number(event.target.value);
                setNewUnits(Number.isNaN(nextValue) ? 0 : nextValue);
              }}
              disabled={!canPartition}
              data-testid="smart-asset-partition-new-input"
              className="w-full rounded-lg border border-sa-border bg-sa-surface px-3 py-2 text-sm text-sa-text"
            />
            <span className="text-xs text-sa-text-muted">Units</span>
          </div>
          {selectedDestination === "generate" && (
            <p className="text-xs text-sa-text-dim mt-2">Generated on confirm.</p>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between text-xs text-sa-text-muted mb-2">
          <span>{sourcePercent}%</span>
          <span>{newPercent}%</span>
        </div>
        <input
          type="range"
          min={inputMin}
          max={inputMax}
          value={sourceUnits}
          onChange={(event) => setSourceUnits(Number(event.target.value))}
          disabled={!canPartition}
          data-testid="smart-asset-partition-slider"
          className="w-full accent-orange-500"
        />
      </div>

      <div className="rounded-lg border border-sa-border bg-sa-surface-hover p-4 mb-6">
        <p className="text-sm text-sa-text">Fee: Battery Charge: 10 Credits</p>
        <p className="text-sm text-sa-text-muted mt-1">SATCHEL Balance: {totalUnits} Units</p>
      </div>

      <div
        className={`rounded-lg border px-4 py-3 text-sm ${
          dustLimitReached
            ? "border-orange-500/40 bg-orange-500/10 text-orange-500"
            : "border-sa-border bg-sa-surface-hover text-sa-text-muted"
        }`}
      >
        {canPartition
          ? "Dust limit enforced: partitions below the minimum carrier UTXO threshold are blocked."
          : "Partitioning unavailable until a unit balance is detected."}
      </div>

      <div className="flex flex-wrap gap-3 mt-6">
        <button
          type="button"
          data-testid="smart-asset-partition-cancel"
          className="px-4 py-2 text-sm font-medium rounded-lg border border-sa-border text-sa-text-muted hover:bg-sa-surface-hover transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="smart-asset-partition-confirm"
          disabled={confirmDisabled}
          aria-disabled={confirmDisabled}
          className="px-5 py-2 text-sm font-medium rounded-lg bg-primary text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-hover"
        >
          Confirm
        </button>
      </div>
    </div>
  );
};

export default PartitionTab;
